import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, MessageCircle, Phone, Pencil, History } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, PageHeader, EmptyState } from "@/components/Ui";
import HistoryDrawer from "@/components/HistoryDrawer";
import { fmtDateTime } from "@/lib/time";
import useRowKeyboardNav from "@/lib/useRowKeyboardNav";

const ICON = { email: Mail, whatsapp: MessageCircle, telegram: MessageCircle, sms: Phone };

export default function Communications() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ client_id: "", channel: "whatsapp", subject: "", body: "" });
    const [saving, setSaving] = useState(false);
    const [edit, setEdit] = useState(null);
    const [editSaving, setEditSaving] = useState(false);
    const [historyFor, setHistoryFor] = useState(null);

    const load = async () => {
        try {
            const [c, cl] = await Promise.all([api.get("/communications"), api.get("/clients")]);
            setItems(c.data); setClients(cl.data);
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await api.post("/communications", form);
            toast.success("Sent");
            setOpen(false); load();
            setForm({ client_id: "", channel: "whatsapp", subject: "", body: "" });
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const openEdit = (c) => setEdit({
        id: c.id,
        subject: c.subject || "",
        body: c.body || "",
        reason: "",
        _orig: c,
    });

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!edit.reason || edit.reason.trim().length < 3) {
            toast.error("Remarks are mandatory (minimum 3 characters)"); return;
        }
        const { id, reason, _orig, ...payload } = edit;
        const diff = {};
        Object.keys(payload).forEach(k => {
            if ((payload[k] ?? "") !== (_orig[k] ?? "")) diff[k] = payload[k];
        });
        if (Object.keys(diff).length === 0) { toast.error("No fields changed"); return; }
        setEditSaving(true);
        try {
            await api.patch(`/communications/${id}`, diff, { headers: { "X-Change-Reason": reason.trim() } });
            toast.success("Message updated · logged to audit trail");
            setEdit(null);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setEditSaving(false); }
    };

    const { getRowProps } = useRowKeyboardNav({ items, onOpen: (c) => openEdit(c), disabled: !!edit || open || !!historyFor });

    return (
        <>
            <PageHeader
                title="Communication center"
                subtitle="Templates, broadcasts and one-to-one — across email, WhatsApp, Telegram and SMS."
                action={<GoldButton onClick={() => setOpen(true)} data-testid="comms-add-btn"><Send className="w-4 h-4 inline mr-2" strokeWidth={2}/>Compose</GoldButton>}
            />

            {items.length === 0 ? (
                <EmptyState title="No messages" subtitle="Your outbound log will appear here." action={<GoldButton onClick={() => setOpen(true)} data-testid="comms-empty-add">Compose</GoldButton>}/>
            ) : (
                <div className="border border-border divide-y divide-border">
                    {items.map((c, idx) => {
                        const Icon = ICON[c.channel] || Mail;
                        return (
                            <div key={c.id} {...getRowProps(idx)}
                                 onClick={(e) => { if ((e.target.closest('button,a'))) return; openEdit(c); }}
                                 className="p-4 sm:px-5 sm:py-4 flex gap-3 sm:gap-4 cursor-pointer hover:bg-muted/50 transition-colors"
                                 data-testid={`comm-row-${c.id}`}>
                                <Icon className="w-5 h-5 text-primary mt-0.5 shrink-0" strokeWidth={1.5}/>
                                <div className="flex-1 min-w-0">
                                    {/* Subject row — subject wraps on mobile, timestamp drops below on narrow screens */}
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-3">
                                        <div className="text-sm text-foreground line-clamp-2 sm:line-clamp-1 min-w-0 flex-1">
                                            {c.subject || c.body?.slice(0, 60)}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground font-mono shrink-0 whitespace-nowrap">
                                            {fmtDateTime(c.sent_at || c.created_at)}
                                        </div>
                                    </div>
                                    {/* Body preview */}
                                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.body}</div>
                                    {/* Meta strip — channel badge + actions (full-width divider on mobile, inline on sm+) */}
                                    <div className="mt-3 pt-3 sm:pt-2 border-t sm:border-t-0 border-border/60 flex items-center justify-between gap-3 flex-wrap">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-primary">{c.channel}</div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <button onClick={() => openEdit(c)} data-testid={`comm-edit-${c.id}`}
                                                className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                                                <Pencil className="w-3 h-3" strokeWidth={1.5}/>Edit
                                            </button>
                                            <button onClick={() => setHistoryFor(c)} data-testid={`comm-history-${c.id}`}
                                                className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                                                <History className="w-3 h-3" strokeWidth={1.5}/>History
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="Compose message" testId="comm-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Client">
                        <Select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} data-testid="comm-client-select"
                            options={[{ value: "", label: "— select (optional) —" }, ...clients.map(c => ({ value: c.id, label: c.name }))]}/>
                    </Field>
                    <Field label="Channel">
                        <Select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} options={["whatsapp", "email", "telegram", "sms"]}/>
                    </Field>
                    {form.channel === "email" && (
                        <Field label="Subject"><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}/></Field>
                    )}
                    <Field label="Message"><Textarea required rows="5" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="comm-body-input"/></Field>
                    <div className="flex justify-end pt-2"><GoldButton disabled={saving} data-testid="comm-send-btn">{saving ? "Sending…" : "Send"}</GoldButton></div>
                </form>
            </Modal>
            <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit message" testId="comm-edit-modal">
                {edit && (
                    <form onSubmit={saveEdit} className="space-y-4">
                        <Field label="Subject"><Input value={edit.subject} onChange={e => setEdit({ ...edit, subject: e.target.value })} data-testid="comm-edit-subject"/></Field>
                        <Field label="Message"><Textarea required rows="5" value={edit.body} onChange={e => setEdit({ ...edit, body: e.target.value })} data-testid="comm-edit-body"/></Field>
                        <Field label="Remarks (mandatory · logged to audit trail)" hint="Explain why this edit is being made. Minimum 3 characters.">
                            <Textarea rows="2" value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} data-testid="comm-edit-reason" placeholder="e.g. Corrected hearing date typo"/>
                        </Field>
                        <div className="text-[10px] text-muted-foreground">Editing only updates the stored copy of the message — already-delivered messages cannot be recalled.</div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEdit(null)} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
                            <GoldButton type="submit" disabled={editSaving} data-testid="comm-edit-save-btn">{editSaving ? "Saving…" : "Save changes"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>

            <HistoryDrawer open={!!historyFor} onClose={() => setHistoryFor(null)} entity="communication" entityId={historyFor?.id} title={(historyFor?.subject || historyFor?.body || "").slice(0, 80)} />
        </>
    );
}